import { useEffect, useRef, useState } from 'react';
import { getCandidateAttackPrompt, getCandidateConversationalAttackPrompt, getCandidatePrompt, getCandidateRebuttalPrompt, getQuestionPrompt, getStreamAiResponse } from '../openai';
import Questions from './Questions';
import { Candidate, QuestionType } from '../types';
import _ from "lodash";

export default function DebatePage(
{
  debatingCandidates,
  backToSelect
}:
{
  debatingCandidates: Candidate[];
  backToSelect: () => void;
}) {
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [showModeratorText, setShowModeratorText] = useState(false);
  const [showThinkingText, setShowThinkingText] = useState(false);
  const [moderatorQuestion, setModeratorQuestion] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [opposingCandidate, setOpposingCandidate] = useState<Candidate | null>(null);
  const [candidateResponse, setCandidateResponse] = useState("");
  const [opposingRebuttal, setOpposingRebuttal] = useState("");
  const [showAiDetectedText, setShowAiDetectedText] = useState(false);
  const [firstAttack, setFirstAttack] = useState("");
  const [secondAttack, setSecondAttack] = useState("");
  const [firstConversational, setFirstConversational] = useState("");
  const [secondConversational, setSecondConversational] = useState("");
  const [showModeratorEndMessage, setShowModeratorEndMessage] = useState(false);
  const [responding, setResponding] = useState(0);
  const [end, setEnd] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  const endRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  const resetAll = () => {
    setLoadingQuestion(false);
    setShowModeratorText(false);
    setShowThinkingText(false);
    setModeratorQuestion("");
    setSelectedCandidate(null);
    setOpposingCandidate(null);
    setCandidateResponse("");
    setOpposingRebuttal("");
    setShowAiDetectedText(false);
    setFirstAttack("");
    setSecondAttack("");
    setFirstConversational("");
    setSecondConversational("");
    setShowModeratorEndMessage(false);
    setResponding(0);
    setEnd(false);
  }

  const getSecondConversational = async (candidate: Candidate, opposition: Candidate, response: string, rebuttal: string) => {
    const prompt = getCandidateConversationalAttackPrompt(candidate.name, opposition.name, response, rebuttal);
    const stream = await getStreamAiResponse(prompt);

    let fullResponse = "";
    for await (const chunk of stream) {
      const newChunk = chunk.choices[0]?.delta?.content || "";
      const newResponse = fullResponse + newChunk;
      fullResponse = newResponse;
      setSecondConversational(fullResponse);
    }
    setShowModeratorEndMessage(true);
    setEnd(true);
    setResponding(0);
  }

  const getFirstConversational = async (candidate: Candidate, opposition: Candidate, response: string, rebuttal: string) => {
    const prompt = getCandidateConversationalAttackPrompt(candidate.name, opposition.name, response, rebuttal);
    const stream = await getStreamAiResponse(prompt);

    let fullResponse = "";
    for await (const chunk of stream) {
      const newChunk = chunk.choices[0]?.delta?.content || "";
      const newResponse = fullResponse + newChunk;
      fullResponse = newResponse;
      setFirstConversational(fullResponse);
    }

    await getSecondConversational(opposition, candidate, rebuttal, fullResponse);
  }

  const getSecondAttack = async (candidate: Candidate, opposition: Candidate, response: string, rebuttal: string) => {
    const prompt = getCandidateAttackPrompt(candidate.name, opposition.name, response, rebuttal);
    const stream = await getStreamAiResponse(prompt);

    let fullResponse = "";
    for await (const chunk of stream) {
      const newChunk = chunk.choices[0]?.delta?.content || "";
      const newResponse = fullResponse + newChunk;
      fullResponse = newResponse;
      setSecondAttack(fullResponse);
    }

    await getFirstConversational(opposition, candidate, rebuttal, fullResponse);
  }

  const getFirstAttack = async (candidate: Candidate, opposition: Candidate, response: string, rebuttal: string) => {
    const prompt = getCandidateAttackPrompt(candidate.name, opposition.name, response, rebuttal);
    const stream = await getStreamAiResponse(prompt);

    let fullResponse = "";
    for await (const chunk of stream) {
      const newChunk = chunk.choices[0]?.delta?.content || "";
      const newResponse = fullResponse + newChunk;
      fullResponse = newResponse;
      setFirstAttack(fullResponse);
    }

    await getSecondAttack(opposition, candidate, rebuttal, fullResponse);
  }

  const getRebuttal = async (candidate: Candidate, opposition: Candidate, question: string, response: string, aiTriggered: boolean) => {
    setShowAiDetectedText(aiTriggered);
    setEnd(false);

    const prompt = getCandidateRebuttalPrompt(candidate.name, opposition.name, question, response);
    const stream = await getStreamAiResponse(prompt);

    let textResponse = "";
    let fullResponse = "";
    let saveToTextResponse: "noContent" | "saving" | "finished" = "noContent";

    for await (const chunk of stream) {
      const newChunk = chunk.choices[0]?.delta?.content || "";

      const newResponse = fullResponse + newChunk;
      fullResponse = newResponse.replace("\n", "");

      if (saveToTextResponse === "saving") {
        const newTextResponse = textResponse + newChunk;
        textResponse = newTextResponse;
        
        if (textResponse.indexOf(`"`) !== -1) {
          textResponse = textResponse.split(`"`)[0];
          saveToTextResponse = "finished";
        }
        setOpposingRebuttal(textResponse);
      } else if (saveToTextResponse === "noContent" && fullResponse.startsWith(`{  "response": "`)) {
        saveToTextResponse = "saving";
        textResponse = fullResponse.split(`{  "response": "`)[1];
        setOpposingRebuttal(textResponse);
      }
    }

    await getFirstAttack(opposition, candidate, response, textResponse);
  }

  const getCandidateResponse = async (candidate: Candidate, opposition: Candidate, question: string) => {
    setShowThinkingText(true);

    const prompt = getCandidatePrompt(candidate.name, opposition.name, question);
    const stream = await getStreamAiResponse(prompt);

    setShowThinkingText(false);

    let textResponse = "";
    let fullResponse = "";
    let saveToTextResponse: "noContent" | "saving" | "finished" = "noContent";
    
    for await (const chunk of stream) {
      const newChunk = chunk.choices[0]?.delta?.content || "";

      const newResponse = fullResponse + newChunk;
      fullResponse = newResponse.replace("\n", "");

      if (saveToTextResponse === "saving") {
        const newTextResponse = textResponse + newChunk;
        textResponse = newTextResponse;
        
        if (textResponse.indexOf(`"`) !== -1) {
          textResponse = textResponse.split(`"`)[0];
          saveToTextResponse = "finished";
        }
        setCandidateResponse(textResponse);
      } else if (saveToTextResponse === "noContent" && fullResponse.startsWith(`{  "response": "`)) {
        saveToTextResponse = "saving";
        textResponse = fullResponse.split(`{  "response": "`)[1];
        setCandidateResponse(textResponse);
      }
    }

    const parsedResponse = JSON.parse(fullResponse);

    if (parsedResponse['attacking_candidate'] !== "NOONE") {
      await getRebuttal(opposition, candidate, question, textResponse, true);
    } else {
      setEnd(true);
      setResponding(0);
    }
  }

  const selectQuestion = async (question: string, type: QuestionType, candidate: Candidate, useExact: boolean, qId: number) => {
    resetAll();
    
    setShowModeratorText(true);

    setSelectedCandidate(candidate);
    const opposition = _.xor(debatingCandidates, [candidate])[0];
    setOpposingCandidate(opposition);
    setModeratorQuestion("");
    setCandidateResponse("");
    setOpposingRebuttal("");
    setShowAiDetectedText(false);
    setLoadingQuestion(true);
    setResponding(qId);

    if (useExact) {
      setModeratorQuestion(question);
      setLoadingQuestion(false);
      await getCandidateResponse(candidate, opposition, question);
    } else {
      const prompt = getQuestionPrompt(candidate.name, question, type);
      const stream = await getStreamAiResponse(prompt);
  
      let pastChunks = "";
      for await (const chunk of stream) {
        const newChunk = chunk.choices[0]?.delta?.content || "";
        const newQuestion = pastChunks + newChunk;
        pastChunks = newQuestion;
        setModeratorQuestion(pastChunks);
      }

      setLoadingQuestion(false);
      await getCandidateResponse(candidate, opposition, pastChunks);
    }
  }

  const getSelectButtonStyles = () => {
    if (responding !== 0 && !end) {
      return "bg-gray-200 text-gray-400 shadow-none";
    } else {
      return "bg-sky-50 text-sky-600 hover:bg-sky-100 hover:text-sky-700"
    }
  }

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
    
    const isMobileDevice = mobileKeywords.some(keyword => userAgent.includes(keyword));

    setIsMobile(isMobileDevice);

    if (isMobileDevice) {
      scrollToBottom()
    }
  }, [moderatorQuestion, candidateResponse, opposingRebuttal, showAiDetectedText, firstAttack, secondAttack, firstConversational, secondConversational, showModeratorEndMessage]);

  return (
    <div className="container m-auto py-8 flex flex-col-reverse md:flex-row gap-6 md:gap-12 px-4 h-screen md:h-auto justify-between md:justify-start">
      <Questions
        candidates={debatingCandidates}
        selectQuestionCallback={selectQuestion}
        responding={responding}
        selectedCandidate={selectedCandidate}
      />
      <div className="w-full pt-2 flex flex-col gap-2.5 overflow-auto md:overflow-visible">
        <div className="flex gap-4 items-center self-stretch mb-4 h-4 whitespace-nowrap font-medium text-2xl text-blue-500">
          <div className="h-0.5 w-full flex-grow bg-gray-200"></div>
          2024 Presidential Debate
          <div className="h-0.5 w-full flex-grow bg-gray-200"></div>
        </div>
        <div className="w-full flex justify-center gap-2">
          {responding === 0 && (
            <button
              className={`${getSelectButtonStyles()} group flex items-center rounded-md text-sm font-medium pl-2 pr-3 py-2 shadow-sm`}
              disabled={responding !== 0 && !end}
              onClick={() => backToSelect()}
            >
              Select different candidates
            </button>
          )}
        </div>
        {showModeratorText && (
          <div className="flex flex-col gap-1.5">
            <div className="text-slate-900 tracking-tight md:text-xl font-medium sliding-text">
              Moderator:
            </div>
            <div className="md:text-lg font-light">{moderatorQuestion}{loadingQuestion ? " ..." : ""}</div>
          </div>
        )}
        {showThinkingText && (
          <div className="italic text-gray-600">{selectedCandidate?.name} is thinking...</div>
        )}
        {candidateResponse && (
          <div className="flex flex-col gap-1.5">
            <div className="text-slate-900 tracking-tight md:text-xl font-medium sliding-text">
              {selectedCandidate?.name}:
            </div>
            <div className="md:text-lg font-light">{candidateResponse}</div>
          </div>
        )}
        {showAiDetectedText && (
          <div className="italic text-blue-500 text-sm">AI Detected that {selectedCandidate?.name} mentioned {opposingCandidate?.name} in their response. Both will be given the opportunity to respond.</div>
        )}
        {opposingRebuttal && (
          <div className="flex flex-col gap-1.5">
            <div className="text-slate-900 tracking-tight md:text-xl font-medium sliding-text">
              {opposingCandidate?.name}:
            </div>
            <div className="md:text-lg font-light">{opposingRebuttal}</div>
          </div>
        )}
        {firstAttack && (
          <div className="flex flex-col gap-1.5">
            <div className="text-slate-900 tracking-tight md:text-xl font-medium sliding-text">
              {selectedCandidate?.name}:
            </div>
            <div className="md:text-lg font-light">{firstAttack}</div>
          </div>
        )}
        {secondAttack && (
          <div className="flex flex-col gap-1.5">
            <div className="text-slate-900 tracking-tight md:text-xl font-medium sliding-text">
              {opposingCandidate?.name}:
            </div>
            <div className="md:text-lg font-light">{secondAttack}</div>
          </div>
        )}
        {firstConversational && (
          <div className="flex flex-col gap-1.5">
            <div className="text-slate-900 tracking-tight md:text-xl font-medium sliding-text">
              {selectedCandidate?.name}:
            </div>
            <div className="md:text-lg font-light">{firstConversational}</div>
          </div>
        )}
        {secondConversational && (
          <div className="flex flex-col gap-1.5">
            <div className="text-slate-900 tracking-tight md:text-xl font-medium sliding-text">
              {opposingCandidate?.name}:
            </div>
            <div className="md:text-lg font-light">{secondConversational}</div>
          </div>
        )}
        {showModeratorEndMessage && (
          <div className="flex flex-col gap-1.5">
            <div className="text-slate-900 tracking-tight md:text-xl font-medium sliding-text">
            Moderator:
            </div>
            <div className="md:text-lg font-light">Alright I'm going to cut you both off right there. We do have a schedule we need to maintain. Thank you both for your responses.</div>
          </div>
        )}
        {end && (
          <div className="flex flex-col gap-4 items-center pb-4 md:pb-0">
            <div className="flex gap-4 items-center self-stretch my-4 h-4 whitespace-nowrap font-semibold text-sm">
              <div className="h-0.5 w-full flex-grow bg-gray-200"></div>
              End
              <div className="h-0.5 w-full flex-grow bg-gray-200"></div>
            </div>
            <div className="flex justify-center items-center gap-2">
              {!opposingRebuttal && <button
                className={`${getSelectButtonStyles()} group flex items-center rounded-md text-sm font-medium pl-2 pr-3 py-2 shadow-sm`}
                disabled={responding !== 0 && !end}
                onClick={() => selectedCandidate && opposingCandidate && getRebuttal(opposingCandidate, selectedCandidate, moderatorQuestion, candidateResponse, false)}
              >
                Hear Rebuttal
              </button>}
              <button
                className={`${getSelectButtonStyles()} group flex items-center rounded-md text-sm font-medium pl-2 pr-3 py-2 shadow-sm`}
                disabled={responding !== 0 && !end}
                onClick={() => backToSelect()}
              >
                Select different candidates
              </button>
            </div>
          </div>
        )}
        <div ref={endRef}></div>
      </div>
    </div>
  );
}

// TODO: add option to continue conversation/ask follow up/prompt followup